import { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { makeStyles, Typography, IconButton } from '@material-ui/core';
import CloudUploadOutlinedIcon from '@material-ui/icons/CloudUploadOutlined';
import InsertDriveFileOutlinedIcon from '@material-ui/icons/InsertDriveFileOutlined';
import CloseIcon from '@material-ui/icons/Close';
import { useSelector } from 'react-redux';
import RoundedButton from 'components/Shared/RoundedButton';

const useStyles = makeStyles(theme => ({
  box: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(3),
    marginBottom: theme.spacing(2),
  },
  title: { fontWeight: 600 },
  subtitle: { color: theme.palette.text.secondary, fontSize: 13, marginBottom: theme.spacing(2) },
  dropZone: {
    border: `1px dashed ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(4),
    textAlign: 'center',
    cursor: 'pointer',
    marginBottom: theme.spacing(2),
    transition: 'border-color 0.2s, background-color 0.2s',
    '&:hover': { borderColor: theme.palette.primary.main },
  },
  dragOver: { borderColor: theme.palette.primary.main, backgroundColor: theme.palette.action.hover },
  dropIcon: { color: theme.palette.text.secondary },
  dropHintSub: { color: theme.palette.text.secondary, fontSize: 12, marginTop: theme.spacing(0.5) },
  // selected-file row
  fileRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(1.5, 2),
    marginBottom: theme.spacing(2),
  },
  fileIcon: { color: theme.palette.primary.main },
  fileMeta: { flex: 1, minWidth: 0 },
  fileName: { fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  fileChange: { color: theme.palette.primary.main, fontSize: 12, cursor: 'pointer', width: 'fit-content' },
  // actions
  actions: { display: 'flex', gap: theme.spacing(2) },
  action: { flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: theme.spacing(0.5) },
  actionBtn: { width: '100%' },
  actionHint: { color: theme.palette.text.secondary, fontSize: 12, textAlign: 'center' },
  hiddenInput: { display: 'none' },
}));

const isXlsx = file => /\.xlsx$/i.test(file?.name || '');

const ClaimUploadBox = ({ onSubmit, loadingMode }) => {
  const classes = useStyles();
  const inputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  // Either action in flight disables both buttons; the spinner shows only
  // on the button whose action is actually running.
  const isLoading = Boolean(loadingMode);
  const {
    en: { claimUpload },
  } = useSelector(state => state.i18n);

  const openPicker = () => {
    // Reset the value first so picking the SAME file again still fires
    // onChange — a file input emits no change event when its value is
    // unchanged, which otherwise breaks "remove then re-add the same file".
    if (inputRef.current) inputRef.current.value = '';
    inputRef.current?.click();
  };

  const pickFile = next => {
    if (!next) return;
    if (!isXlsx(next)) {
      setFile(null);
      onSubmit.onInvalid?.();
      return;
    }
    setFile(next);
  };

  const handleDrop = e => {
    e.preventDefault();
    setDragOver(false);
    pickFile(e.dataTransfer.files?.[0]);
  };

  return (
    <div className={classes.box}>
      <Typography className={classes.title}>{claimUpload.title}</Typography>
      <Typography className={classes.subtitle}>{claimUpload.boxSubtitle}</Typography>

      {file ? (
        <div className={classes.fileRow}>
          <InsertDriveFileOutlinedIcon className={classes.fileIcon} />
          <div className={classes.fileMeta}>
            <Typography className={classes.fileName} title={file.name}>
              {file.name}
            </Typography>
            <Typography className={classes.fileChange} onClick={openPicker}>
              {claimUpload.changeFile}
            </Typography>
          </div>
          <IconButton
            size="small"
            onClick={() => setFile(null)}
            disabled={isLoading}
            title={claimUpload.removeFile}
            aria-label={claimUpload.removeFile}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </div>
      ) : (
        <div
          className={`${classes.dropZone} ${dragOver ? classes.dragOver : ''}`}
          onClick={openPicker}
          onDragOver={e => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
        >
          <CloudUploadOutlinedIcon fontSize="large" className={classes.dropIcon} />
          <Typography>{claimUpload.dropHint}</Typography>
          <Typography className={classes.dropHintSub}>{claimUpload.dropHintSub}</Typography>
        </div>
      )}

      <input
        ref={inputRef}
        type="file"
        accept=".xlsx"
        className={classes.hiddenInput}
        onChange={e => pickFile(e.target.files?.[0])}
      />

      <div className={classes.actions}>
        <div className={classes.action}>
          <RoundedButton
            variant="outlined"
            color="primary"
            size="small"
            className={classes.actionBtn}
            disabled={!file || isLoading}
            isLoading={loadingMode === 'validate'}
            onClick={() => onSubmit.run(file, { validateOnly: true })}
          >
            {claimUpload.validateOnly}
          </RoundedButton>
          <Typography className={classes.actionHint}>{claimUpload.validateOnlyHint}</Typography>
        </div>
        <div className={classes.action}>
          <RoundedButton
            variant="contained"
            color="primary"
            size="small"
            className={classes.actionBtn}
            disabled={!file || isLoading}
            isLoading={loadingMode === 'import'}
            onClick={() => onSubmit.run(file, { validateOnly: false })}
          >
            {claimUpload.uploadAndImport}
          </RoundedButton>
          <Typography className={classes.actionHint}>{claimUpload.uploadAndImportHint}</Typography>
        </div>
      </div>
    </div>
  );
};

ClaimUploadBox.propTypes = {
  onSubmit: PropTypes.shape({
    run: PropTypes.func.isRequired,
    onInvalid: PropTypes.func,
  }).isRequired,
  loadingMode: PropTypes.oneOf(['validate', 'import']),
};

ClaimUploadBox.defaultProps = { loadingMode: null };

export default ClaimUploadBox;
